import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Textarea,
  toast
} from "@veritrellis/ui";
import {
  ArrowUpRight,
  CreditCard,
  ExternalLink,
  Info,
  ShieldCheck,
  ShoppingCart
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { apiFetch } from "../lib/api.js";
import { marketingLegalUrl, marketingOrigin } from "../lib/marketingSite.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";
import { useDisclosure } from "../hooks/useDisclosure.js";

interface BillingData {
  planKey: string;
  planLabel: string;
  billingStatus: string;
  hasStripeCustomer: boolean;
  stripeBillingEnabled: boolean;
  organizationId: string;
  checkoutPlans: Array<{
    priceId: string;
    label: string;
    baseUsd: number;
    interval: string | null;
    planKey: string;
    currency: string;
  }>;
  seatCount: number;
  workspaceSeatCount: number;
  workspaceCount: number;
  usage: {
    productionControlledActions: number;
    sandboxEvaluations: number;
    includedProduction: number;
    includedSandbox: number;
    windowDays: number;
  };
  entitlements: Record<string, unknown>;
  upgradePrompts: string[];
}

const PLAN_ORDER = ["free", "starter", "pro", "enterprise"];

const PLAN_OPTIONS = [
  { value: "starter", label: "Starter — $49/mo + add-ons" },
  { value: "pro", label: "Pro — $299/mo + add-ons" },
  { value: "enterprise", label: "Enterprise — custom" }
];

const planBadgeVariant = (key: string): "secondary" | "default" | "warning" | "outline" => {
  if (key === "free") return "secondary";
  if (key === "starter") return "default";
  if (key === "pro") return "outline";
  return "warning";
};

const statusBadgeVariant = (
  status: string
): "success" | "destructive" | "warning" | "secondary" => {
  if (status === "active") return "success";
  if (status === "trialing") return "success";
  if (status === "past_due") return "warning";
  if (status === "canceled") return "destructive";
  return "secondary";
};

const formatStripeMoney = (amountMajor: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0
  }).format(amountMajor);

const formatBillingInterval = (interval: string | null) => {
  if (interval === "month") return "mo";
  if (interval === "year") return "yr";
  if (interval === "week") return "wk";
  return interval ?? "term";
};

const EntitlementCard = (input: { label: string; value: unknown }) => {
  const display =
    input.value === null || input.value === undefined
      ? "—"
      : typeof input.value === "boolean"
        ? input.value
          ? "Included"
          : "Not included"
        : input.value === -1 || input.value === "unlimited"
          ? "Unlimited"
          : String(input.value);
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-card-body">
        <p className="admin-stat-card-label">{input.label}</p>
        <p className="admin-stat-card-value" style={{ fontSize: "1.125rem" }}>
          {display}
        </p>
      </div>
    </div>
  );
};

const UsageMeter = (input: { label: string; current: number; limit?: number | null }) => {
  const pct =
    input.limit !== undefined && input.limit !== null && input.limit > 0
      ? Math.min(100, (input.current / input.limit) * 100)
      : null;
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm">
        <span>{input.label}</span>
        <span className="font-semibold">
          {input.current.toLocaleString()}
          {input.limit !== undefined && input.limit !== null
            ? ` / ${input.limit === -1 ? "∞" : input.limit.toLocaleString()}`
            : ""}
        </span>
      </div>
      {pct !== null ? (
        <Progress value={pct} className="h-2" />
      ) : (
        <Progress value={0} className="h-2 opacity-30" />
      )}
    </div>
  );
};

const UpgradeModal = (input: {
  opened: boolean;
  onClose: () => void;
  workspaceId: string;
  currentPlanKey: string;
}) => {
  const [requestedPlan, setRequestedPlan] = useState("");
  const [notes, setNotes] = useState("");
  const [planError, setPlanError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async (values: { requestedPlan: string; notes: string }) => {
      const res = await apiFetch(`/admin/workspaces/${input.workspaceId}/billing/upgrade-request`, {
        method: "POST",
        body: JSON.stringify(values)
      });
      if (!res.ok) throw new Error("Request failed");
      return (await res.json()) as { message: string };
    },
    onSuccess: (data) => {
      toast.show({ title: "Request sent", message: data.message, color: "green" });
      input.onClose();
      setRequestedPlan("");
      setNotes("");
      setPlanError(null);
    },
    onError: () => {
      toast.show({
        title: "Error",
        message: "Could not submit request. Try again.",
        color: "red"
      });
    }
  });

  const upgradablePlans = PLAN_OPTIONS.filter(
    (p) => PLAN_ORDER.indexOf(p.value) > PLAN_ORDER.indexOf(input.currentPlanKey)
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestedPlan) {
      setPlanError("Choose a plan");
      return;
    }
    setPlanError(null);
    submit.mutate({ requestedPlan, notes });
  };

  return (
    <Dialog open={input.opened} onOpenChange={(open) => !open && input.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a plan change</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-3">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Our team will review your request and reach out within 1 business day.
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-1.5">
              <Label>Requested plan</Label>
              <Select value={requestedPlan} onValueChange={setRequestedPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {(upgradablePlans.length > 0 ? upgradablePlans : PLAN_OPTIONS).map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {planError ? <p className="text-xs text-destructive">{planError}</p> : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Expected volume, custom needs, questions about pricing…"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
              />
            </div>
          </div>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={input.onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submit.isPending}>
              {submit.isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
              <ArrowUpRight className="mr-1 h-4 w-4" />
              Send request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ENTITLEMENT_LABELS: Record<string, string> = {
  included_production_controlled_actions_per_month: "Production CA / mo",
  included_sandbox_evaluations_per_month: "Sandbox evals / mo",
  included_members: "Included members",
  max_members: "Max members",
  max_extra_paid_members: "Extra paid members",
  included_workspaces: "Included workspaces",
  max_workspaces: "Max workspaces",
  max_extra_paid_workspaces: "Extra paid workspaces",
  max_action_types: "Action types",
  max_active_production_policies: "Active prod policies",
  audit_retention_days: "Audit retention (days)",
  feature_approval_groups: "Approval groups",
  feature_escalation_rules: "Escalation rules",
  feature_audit_export: "Audit export",
  feature_sso: "SSO",
  feature_scim: "SCIM",
  feature_advanced_rbac: "Advanced RBAC",
  feature_audit_streaming: "Audit streaming",
  feature_sla: "SLA",
  feature_invoice_billing: "Invoice billing"
};

export const SettingsBillingPage = () => {
  const { workspaceId, isLoading: wsLoading } = useWorkspaceId();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [upgradeOpened, { open: openUpgrade, close: closeUpgrade }] = useDisclosure(false);

  const billingQ = useQuery({
    queryKey: ["billing", workspaceId],
    queryFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/billing`);
      if (!res.ok) throw new Error("Failed to load billing");
      return (await res.json()) as BillingData;
    },
    enabled: !!workspaceId
  });

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout !== "success" && checkout !== "cancel") {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
    if (checkout === "success") {
      toast.show({
        title: "Checkout complete",
        message: "Your plan and billing profile are updating. This can take a few seconds.",
        color: "green"
      });
      if (workspaceId) {
        void queryClient.invalidateQueries({ queryKey: ["billing", workspaceId] });
      }
    } else {
      toast.show({
        title: "Checkout canceled",
        message: "No changes were made. You can subscribe again whenever you are ready.",
        color: "yellow"
      });
    }
  }, [searchParams, setSearchParams, workspaceId, queryClient]);

  const portalMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/billing/portal`, {
        method: "POST"
      });
      if (!res.ok) throw new Error("Portal unavailable");
      return (await res.json()) as { url: string };
    },
    onSuccess: (data) => {
      window.open(data.url, "_blank", "noopener,noreferrer");
    },
    onError: () => {
      toast.show({
        title: "Error",
        message: "Could not open billing portal. Contact support.",
        color: "red"
      });
    }
  });

  const checkoutMutation = useMutation({
    mutationFn: async (priceId: string) => {
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/billing/checkout-session`, {
        method: "POST",
        body: JSON.stringify({ priceId })
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? "checkout_failed");
      }
      return (await res.json()) as { url: string };
    },
    onSuccess: (data) => {
      window.location.assign(data.url);
    },
    onError: () => {
      toast.show({
        title: "Checkout unavailable",
        message:
          "Checkout failed (invalid price, already on a paid plan, or no eligible Stripe products). Ensure Stripe products have self-serve metadata — see deployment docs.",
        color: "red"
      });
    }
  });

  if (wsLoading) return null;
  if (!workspaceId) return <Navigate to="/onboarding" replace />;

  const b = billingQ.data;

  const knownEntitlements = b
    ? Object.entries(ENTITLEMENT_LABELS)
        .filter(([k]) => b.entitlements[k] !== undefined)
        .map(([k, label]) => ({ key: k, label, value: b.entitlements[k] }))
    : [];

  return (
    <div className="admin-page-stack">
      <PageHeader
        title="Plan & usage"
        description="Your organization's subscription, payment method, and included usage. Live amounts come from Stripe."
      />

      <section className="admin-panel" aria-labelledby="billing-plan-heading">
        <div className="admin-panel-header">
          <h2 id="billing-plan-heading" className="admin-panel-title">
            Current plan
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <div className="admin-billing-plan-head">
            <div>
              <p className="admin-billing-plan-title">
                <ShieldCheck className="h-5 w-5 text-primary" style={{ display: "inline" }} />
                {b ? b.planLabel : <Skeleton className="inline-block h-5 w-20" />}
                {b ? (
                  <Badge
                    variant={
                      statusBadgeVariant(b.billingStatus) !== "secondary"
                        ? statusBadgeVariant(b.billingStatus)
                        : planBadgeVariant(b.planKey)
                    }
                  >
                    {b.billingStatus}
                  </Badge>
                ) : (
                  <Skeleton className="inline-block h-5 w-16" />
                )}
              </p>
              {b ? (
                <p className="admin-billing-plan-meta">
                  {b.seatCount} member{b.seatCount === 1 ? "" : "s"} · {b.workspaceCount} workspace
                  {b.workspaceCount === 1 ? "" : "s"}
                </p>
              ) : null}
            </div>
            <div className="admin-billing-actions">
              {b?.stripeBillingEnabled ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={portalMutation.isPending}
                  onClick={() => portalMutation.mutate()}
                >
                  <CreditCard className="mr-1 h-4 w-4" />
                  {b.hasStripeCustomer ? "Payment & billing" : "Add payment method"}
                  <ExternalLink className="ml-1 h-3.5 w-3.5" />
                </Button>
              ) : null}
              <Button variant="secondary" size="sm" onClick={openUpgrade}>
                <ArrowUpRight className="mr-1 h-4 w-4" />
                {b?.planKey === "enterprise" ? "Request changes" : "Upgrade"}
              </Button>
            </div>
          </div>

          {b && b.upgradePrompts.length > 0 ? (
            <Alert className="border-amber-200 bg-amber-50" style={{ marginTop: "1rem" }}>
              <AlertTitle>Usage notice</AlertTitle>
              <AlertDescription>
                <ul className="mt-1 list-inside list-disc text-sm">
                  {b.upgradePrompts.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}
        </div>
      </section>

      {b && b.planKey === "free" && (b.checkoutPlans ?? []).length > 0 ? (
        <section className="admin-panel" aria-labelledby="billing-subscribe-heading">
          <div className="admin-panel-header">
            <h2 id="billing-subscribe-heading" className="admin-panel-title">
              Subscribe
            </h2>
          </div>
          <div className="admin-panel-body-padded">
            <p className="admin-settings-row-desc" style={{ margin: "0 0 1rem" }}>
              Pricing comes live from Stripe. Checkout opens on Stripe and you will return here when
              it completes.
            </p>
            <Alert style={{ marginBottom: "1rem" }}>
              <AlertDescription>
                Veritrellis is a B2B service. Prices exclude VAT unless stated otherwise.
                Subscriptions renew automatically until cancelled. Use is subject to the{" "}
                <a
                  href={marketingLegalUrl("terms")}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  Terms of Service
                </a>
                ,{" "}
                <a
                  href={marketingLegalUrl("privacy")}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  Privacy Notice
                </a>
                , and{" "}
                <a
                  href={marketingLegalUrl("dpa")}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  Data Processing Agreement
                </a>
                .
              </AlertDescription>
            </Alert>
            <div className="admin-billing-actions">
              {(b.checkoutPlans ?? []).map((p) => (
                <Button
                  key={p.priceId}
                  variant="secondary"
                  disabled={checkoutMutation.isPending}
                  onClick={() => checkoutMutation.mutate(p.priceId)}
                >
                  <ShoppingCart className="mr-1 h-4 w-4" />
                  {p.label} — {formatStripeMoney(p.baseUsd, p.currency)}/
                  {formatBillingInterval(p.interval)}
                </Button>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="admin-panel" aria-labelledby="billing-usage-heading">
        <div className="admin-panel-header">
          <h2 id="billing-usage-heading" className="admin-panel-title">
            Usage this period
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          {billingQ.isLoading ? (
            <>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" style={{ marginTop: "1rem" }} />
            </>
          ) : b ? (
            <>
              <UsageMeter
                label="Production controlled actions"
                current={b.usage.productionControlledActions}
                limit={b.usage.includedProduction}
              />
              <div style={{ marginTop: "1rem" }}>
                <UsageMeter
                  label="Sandbox evaluations"
                  current={b.usage.sandboxEvaluations}
                  limit={b.usage.includedSandbox}
                />
              </div>
              <p className="text-xs text-muted-foreground" style={{ marginTop: "1rem" }}>
                Production usage above {b.usage.includedProduction.toLocaleString()} is reported to
                Stripe as overage at the per-action rate of your plan.
              </p>
            </>
          ) : null}
        </div>
      </section>

      {(billingQ.isLoading || knownEntitlements.length > 0) && (
        <section className="admin-panel" aria-labelledby="billing-entitlements-heading">
          <div className="admin-panel-header">
            <h2 id="billing-entitlements-heading" className="admin-panel-title">
              Plan entitlements
            </h2>
          </div>
          <div className="admin-panel-body-padded">
            {billingQ.isLoading ? (
              <div className="admin-entitlements-grid">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-[72px] rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="admin-entitlements-grid">
                {knownEntitlements.map(({ key, label, value }) => (
                  <EntitlementCard key={key} label={label} value={value} />
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground" style={{ marginTop: "1rem" }}>
              See full pricing at{" "}
              <a
                href={`${marketingOrigin}/pricing`}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline"
              >
                veritrellis.ai/pricing
              </a>
              .
            </p>
          </div>
        </section>
      )}

      {workspaceId && b ? (
        <UpgradeModal
          opened={upgradeOpened}
          onClose={closeUpgrade}
          workspaceId={workspaceId}
          currentPlanKey={b.planKey}
        />
      ) : null}
    </div>
  );
};
